/**
 * Как звонить из карточки — те же четыре пути, что у кнопки «Позвонить» веб-CRM
 * (GiperCRM/client/src/components/calls/CallButton.tsx):
 *  • pbx      — АТС настроена и за человеком закреплён сотрудник: АТС сама звонит на его телефон, потом клиенту;
 *  • noLogin  — АТС настроена, а сотрудник не закреплён: объясняем словами и предлагаем обычный звонок;
 *  • device   — АТС нет: обычный звонок с телефона (tel:);
 *  • own      — своя АТС с софтфоном в браузере: в приложении не поддерживается, предлагаем tel:.
 */
import type { CallBrief, TelephonySettings, TelephonyJob } from '@/api/crmCard';

export type CallMode = 'pbx' | 'noLogin' | 'device' | 'own';

export function callMode(s: TelephonySettings | null | undefined): CallMode {
  if (!s) return 'device';
  if (s.provider === 'own') return 'own';
  const pbxOn = !!(s.configured && s.enabled);
  if (!pbxOn) return 'device';
  return s.myPbxLogin ? 'pbx' : 'noLogin';
}

/** Пояснения словами — дословно по смыслу веб-кнопки. */
export const NO_LOGIN_TEXT = 'У вас нет логина АТС — обратитесь к руководителю. Его закрепляют в CRM: «Настройки → Телефония».';
export const OWN_PBX_TEXT = 'Звонок через свою АТС в приложении пока недоступен. Можно позвонить обычным звонком с телефона.';

/** Номер для набора с телефона: плюс и цифры. */
export function telHref(phone: string): string {
  const cleaned = phone.replace(/[^\d+]/g, '');
  return `tel:${cleaned}`;
}

/** Шаги наложения «АТС звонит → соединяем → разговор» (прототип, call overlay). */
export type CallStep = 0 | 1 | 2;

export type CallPhase =
  | { kind: 'working'; step: CallStep }
  | { kind: 'ended' }
  | { kind: 'failed'; message: string };

/**
 * По заданию АТС и последнему звонку сделки решаем, на каком шаге мы.
 *  • задание не принято / упало — ошибка со словами АТС;
 *  • звонка ещё нет (или он старше нашей просьбы) — шаг 0: АТС звонит на телефон человека;
 *  • звонок идёт (ringing) — шаг 1: соединяем с клиентом;
 *  • разговор (in_progress) — шаг 2;
 *  • звонок закончился (completed / no_answer / failed) — конец.
 * Статусы звонков — crm.calls.status (routes/calls.ts → callView). 🪤 На живой АТС порядок статусов
 * не проверялся — см. отчёт: при расхождении наложение остаётся на шаге 0, пока звонок не закончится.
 */
export function callPhaseOf(job: Pick<TelephonyJob, 'status' | 'reason'> | null, call: Pick<CallBrief, 'status' | 'occurredAt' | 'startedAt'> | null, askedAtMs: number): CallPhase {
  if (job?.status === 'failed') return { kind: 'failed', message: job.reason || 'АТС не приняла звонок' };
  if (!call) return { kind: 'working', step: 0 };
  const at = Date.parse(call.startedAt ?? call.occurredAt ?? '');
  // Звонок старше нашей просьбы (минус запас на часы) — это прошлый разговор, не наш.
  if (Number.isFinite(at) && at < askedAtMs - 15_000) return { kind: 'working', step: 0 };
  switch (call.status) {
    case 'in_progress': return { kind: 'working', step: 2 };
    case 'ringing': return { kind: 'working', step: 1 };
    case 'completed':
    case 'no_answer':
    case 'failed': return { kind: 'ended' };
    default: return { kind: 'working', step: 0 };
  }
}

/** Текст запрета звонить, пока не решены итоги прошлых звонков (тот же, что отдаёт сервер при звонке через АТС). */
export function gateMessage(g: { old: number; minutes: number }): string {
  return `Сначала решите по прошлым звонкам: ${g.old} итогов ждут дольше ${g.minutes} минут`;
}
